import { AppVisibility, Platform, SessionStorage, LocalStorage } from "quasar";
import getDeviceInfo, { mobileOrDesktop } from "../device";
import { Record } from "@deepstream/client/dist/src/record/record";
import axios from "axios";

export default class Meeting {
  constructor(deepstream, store) {
    this.store = store;
    this.deepstream = deepstream;
    /**
     * @type {Record}
     */
    this.record = null;
    this.meeting = null;
  }

  async createCode() {
    // I commented this because User may not want to be redirected to an old session, intead they want create a new one
    /*
     if (LocalStorage.has("sid")) {
      mobileOrDesktop("/meeting", {
        sid: encodeURIComponent(LocalStorage.getItem("sid"))
      });
      return;
     }
     */
    const code = await this.getCode();
    const method = `${this.store.state.app.config.cid}-widget-${code}`;
    this.deepstream.client.rpc.provide(method, async (data, response) => {
      switch (data.action) {
        case "get":
          await response.send({
            status: "success",
            data: {
              consumer: {
                uid: this.store.state.meeting.uid,
                device: getDeviceInfo(),
                consent: this.store.state.app.config.consent,
                code: code,
                channel: "code",
              },
            },
          });
          break;
        case "set":
          await response.send({
            status: "success",
            data: "ok",
          });
          if (Platform.is.desktop) {
            LocalStorage.set("sid", data.recordName);
          }

          await axios.delete(`${process.env.API_BASE_URL}/code`, {
            params: {
              cid: this.store.state.app.config.cid,
              code,
            },
          });
          LocalStorage.remove("offline_data");
          mobileOrDesktop(
            "/meeting",
            {
              sid: encodeURIComponent(data.recordName),
              forceOrigin: data.forceOrigin,
            },
            data.url
          );
          break;
        default:
          await response.send({
            status: "error",
            data: "IvalidAction",
          });
      }
    });
    return code;
  }

  async codeExpired(code) {
    const method = `${this.store.state.app.config.cid}-widget-${code}`;
    await this.deepstream.client.rpc.unprovide(method);
    await axios.delete(`${process.env.API_BASE_URL}/code`, {
      params: {
        cid: this.store.state.app.config.cid,
        code,
      },
    });
  }

  async validateMeetingStarted(encodedData) {
    try {
      const linkData = JSON.parse(atob(encodedData));
      const sessionInLink = `${linkData.cid}/${linkData.sid}`;
      const isStarted = await this.deepstream.client.record.has(sessionInLink);

      if (!isStarted) {
        return isStarted;
      }
      const record = this.deepstream.client.record.getRecord(sessionInLink);
      await record.whenReady();
      const data = await record.get();

      return {
        url: data.url,
        forceOrigin: data.forceOrigin,
      };
    } catch (error) {}
  }

  async startMeetingFromLink(encodedData, customerError = false) {
    const started = await this.validateMeetingStarted(encodedData);
    if (!LocalStorage.has("sid") && started) {
      const linkData = JSON.parse(atob(encodedData));
      const sessionInLink = `${linkData.cid}/${linkData.sid}`;
      if (Platform.is.desktop) {
        LocalStorage.set("sid", linkData.sid);
      }
      mobileOrDesktop(
        "/meeting",
        {
          sid: encodeURIComponent(sessionInLink),
          forceOrigin: started.forceOrigin,
        },
        started.url
      );
      return;
    }
    if (LocalStorage.has("sid")) {
      // if there is a session id store in the local storage,
      // it must be figured out if it is the same session or a new one
      const linkData = JSON.parse(atob(encodedData));
      const sessionInLink = `${linkData.cid}/${linkData.sid}`;
      const sessionInLocalStorage = LocalStorage.getItem("sid");
      if (sessionInLink === sessionInLocalStorage) {
        // is the same session
        // it means that the user is trying to join a session that is already active.
        // perhaps the user reloaded the page
        mobileOrDesktop(
          "/meeting",
          {
            sid: encodeURIComponent(LocalStorage.getItem("sid")),
            forceOrigin: started.forceOrigin,
          },
          started.url
        );
        return;
      } else {
        // is not the same session
        // in this case, Agent send a new link, and in local storage there is an old session id
        // it must remove the old session id in order to create a new session

        // remove local storage
        LocalStorage.remove("sid");
        LocalStorage.remove("uid");
        LocalStorage.remove("offline_data");

        // clear meeting data
        this.meeting = null;
        this.store.commit("meeting/reset");
        LocalStorage.remove("sid");
        SessionStorage.set("meetingstatus", "ended");
      }
    }
    const linkData = JSON.parse(atob(encodedData));
    const method = `start-meeting-${linkData.cid}-${linkData.agentUid}-${linkData.sid}`;
    if (customerError) {
      await this.sendCustomerErrors(method);
      return;
    }
    const data = {
      consumer: {
        uid: this.store.state.meeting.uid,
        device: getDeviceInfo(),
        consent: null,
        code: null,
        channel: "link",
      },
    };
    const result = await this.deepstream.send(method, data);

    if (result.error === "Brand not accepted") {
      this.store.commit("app/waitForBrandSelection", true);
    }

    await this.deepstream.client.rpc.provide(
      result.confirm,
      async (data, response) => {
        await response.send({ status: "success", data: "ok" });
        SessionStorage.clear();
        if (Platform.is.desktop) {
          LocalStorage.set("sid", data.recordName);
        }
        mobileOrDesktop(
          "/meeting",
          {
            sid: encodeURIComponent(data.recordName),
            forceOrigin: data.forceOrigin,
          },
          data.url
        );
      }
    );
  }

  async sendCustomerErrors(method) {
    let sid = false;
    if (Platform.is.desktop) {
      sid = LocalStorage.getItem("sid");
    } else {
      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });
      sid = params.sid;
    }
    if (sid) {
      if (!!this.store.state.app.browserInvalid) {
        await this.record?.set("sync.customerError", "browser");
        return;
      }
      if (!!this.store.state.app.screenErrorType) {
        await this.record?.set(
          "sync.customerError",
          this.store.state.app.screenErrorType
        );
        return;
      }
      await this.record?.set("sync.customerError", null);
      return;
    }
    if (!!this.store.state.app.browserInvalid) {
      await this.deepstream.send(method, {
        consumer: {
          uid: this.store.state.meeting.uid,
          status: "error",
          error: "browser",
          channel: "link",
        },
      });
      return;
    }
    if (!!this.store.state.app.screenErrorType) {
      await this.deepstream.send(method, {
        consumer: {
          uid: this.store.state.meeting.uid,
          status: "error",
          error: this.store.state.app.screenErrorType,
          channel: "link",
        },
      });
      return;
    }
  }

  async join(recordName) {
    // console.log("Meeting->join->recordName", recordName);
    const isRecordActive = await this.deepstream.client.record.has(recordName);
    if (isRecordActive) {
      // check firts if there is any record with that namebundleRenderer.renderToStream
      // because getRecord create a new record if recordname not found
      this.record = this.deepstream.client.record.getRecord(recordName);
      await this.record.whenReady();
      this.unsubscribe();
      this.record.setMergeStrategy(
        (record, localVersion, remoteValue, remoteVersion, callback) => {
          callback(null, remoteValue);
        }
      );
      await this.record.set("consumerConnection", new Date().getTime());
      this.meeting = await this.record.get();
      if (!this.meeting) {
        throw new Error("Session no longer active");
      }
      this.subscribe();

      // subscribe store mutations
      this.store.subscribe(async (mutation, state) => {
        await this.record.set("sync.preview.state", state);
      });
    } else {
      // console.log("Session is not active");
      LocalStorage.remove("sid");
      // throw new Error("Session no longer active");
      // this.logout();
      document.location = "end-session";
    }
  }

  subscribe() {
    // subscribe session status
    this.record.subscribe("sync.status", this.syncStatus.bind(this), true);

    //subscribe currentInteraction
    this.record.subscribe(
      "sync.currentInteraction",
      this.syncCurrentInteraction.bind(this),
      true
    );

    //subscribe standby
    this.record.subscribe("sync.standby", this.syncStandby.bind(this), true);

    //subscribe disableUserInteraction
    this.record.subscribe(
      "sync.disableUserInteraction",
      this.syncDisableUserInteraction.bind(this),
      true
    );
  }

  unsubscribe() {
    this.record.unsubscribe("sync.status", this.syncStatus.bind(this));

    this.record.unsubscribe(
      "sync.currentInteraction",
      this.syncCurrentInteraction.bind(this)
    );

    this.record.unsubscribe("sync.standby", this.syncStandby.bind(this));

    this.record.unsubscribe(
      "sync.disableUserInteraction",
      this.syncDisableUserInteraction.bind(this)
    );
  }

  syncStatus(status) {
    if (status === "ended") {
      this.logout();
    }
  }

  async syncCurrentInteraction(currentInteraction) {
    if (currentInteraction?.type === "businessCard") {
      await this.record?.set("sync.openBusinessCard", null);
    }

    if (currentInteraction?.source === "plugin") {
      await this.setCurrentInteractionFormatVersion1(currentInteraction);
      return;
    }

    // Cards API Versioning & Routing
    // -------------------------------------------------------
    // cardsVersion determines which backend service handles the cards/getCard request.
    //
    //   cardsVersion === 2 -> CORE_DATA_SERVICES_API_URL (data-services app in cenora-core-data-services)
    //   cardsVersion === 3 -> AGENT_DATA_SERVICES_API_URL (agency-data-services app in cenora-core-data-services)
    //
    // Both services expose the same cards/getCard endpoint.
    // The env var controls which service is called — no version prefix in the URL.
    if (this.meeting?.cardsVersion === 3) {
      await this.setCurrentInteractionFormatVersion3(currentInteraction);
      return;
    }

    if (this.meeting?.cardsVersion === 2) {
      await this.setCurrentInteractionFormatVersion2(currentInteraction);
      return;
    }

    await this.setCurrentInteractionFormatVersion1(currentInteraction);
  }

  async syncStandby(standby) {
    this.store.commit("meeting/standby", standby);
    if (standby) {
      await this.setContentStatus();
    }
  }

  syncDisableUserInteraction(standby) {
    this.store.commit("meeting/disableUserInteraction", standby);
  }

  async setCurrentInteractionFormatVersion1(currentInteraction) {
    this.store.commit("meeting/currentInteraction", currentInteraction);
    await this.setContentStatus();
  }

  async setCurrentInteractionFormatVersion2(currentInteraction) {
    if (!currentInteraction) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (
      currentInteraction.type === "card" &&
      !currentInteraction?.content?.hash_id
    ) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (currentInteraction.type !== "card") {
      this.store.commit("meeting/currentInteraction", currentInteraction);
      return;
    }

    let baseUrl = process.env.CORE_DATA_SERVICES_API_URL;

    if (
      !!currentInteraction.content.industry &&
      currentInteraction.content.industry !== "cruiseline"
    ) {
      baseUrl += `/${currentInteraction.content.industry}`;
    }

    const response = await axios.post(`${baseUrl}/cards/getCard`, {
      hash_ids: currentInteraction.content.hash_id,
    });
    this.store.commit("meeting/currentInteraction", {
      ...currentInteraction,
      content:
        response.data instanceof Array ? response.data[0] : response.data,
    });
    await this.setContentStatus();
  }

  async setCurrentInteractionFormatVersion3(currentInteraction) {
    if (!currentInteraction) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (
      currentInteraction.type === "card" &&
      !currentInteraction?.content?.hash_id
    ) {
      this.store.commit("meeting/currentInteraction", null);
      return;
    }

    if (currentInteraction.type !== "card") {
      this.store.commit("meeting/currentInteraction", currentInteraction);
      return;
    }

    let baseUrl = process.env.AGENT_DATA_SERVICES_API_URL;

    // Industry-based URL prefixing is disabled for v3 — agency-data-services
    // handles all industries at a single endpoint without a path prefix.
    // if (
    //   !!currentInteraction.content.industry &&
    //   currentInteraction.content.industry !== "cruiseline"
    // ) {
    //   baseUrl += `/${currentInteraction.content.industry}`;
    // }

    const response = await axios.post(`${baseUrl}/cards/getCard`, {
      hash_ids: currentInteraction.content.hash_id,
    });
    this.store.commit("meeting/currentInteraction", {
      ...currentInteraction,
      content:
        response.data instanceof Array ? response.data[0] : response.data,
    });
    await this.setContentStatus();
  }

  async cardStateChanged(state) {
    await this.record.set("sync.currentInteractionState", state);
  }

  async setContentStatus() {
    let status = "delivered";
    if (AppVisibility.appVisible) {
      status = "seen";
    }

    await this.record.set("sync.currentInteractionStatus", status);
  }

  saveOfflineData() {
    const offlineData = {
      sid: this.meeting.sid,
      agent: {
        email: this.meeting.agent.email,
        firstName: this.meeting.agent.firstName,
        lastName: this.meeting.agent.lastName,
        phoneNumber: this.meeting.agent.phoneNumber,
        phoneNumberPrivate: this.meeting.agent.phoneNumberPrivate,
        nickname: this.meeting.agent.nickname,
        title: this.meeting.agent.title,
        account_cid: this.meeting.agent.account_cid,
        account_id: this.meeting.agent.account_id,
        account_parent_id: this.meeting.agent.account_parent_id,
        account_parent_cid: this.meeting.agent.account_parent_cid,
      },
      brand: Meeting.getInstance().record?.record.data.brand,
      salesforce: Meeting.getInstance().record?.record.data.salesforce,
      review: false,
      forceOrigin: LocalStorage.getItem("forceOrigin"),
    };
    this.store.commit("app/offlineData", offlineData);
  }

  async logout() {
    this.saveOfflineData();
    this.meeting = null;
    this.store.commit("meeting/reset");
    LocalStorage.remove("sid");
    SessionStorage.set("meetingstatus", "ended");
    this.deepstream.logout();
    // document.location = "end-session";
  }

  async getCode() {
    const response = await axios.get(`${process.env.API_BASE_URL}/code`, {
      params: {
        cid: this.store.state.app.config.cid,
      },
    });

    return response.data.data;
  }
}
